import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Loader2 } from "lucide-react";
import { useUserInfo } from "../../context/user";
import { useUsersContext } from "../../context/usersContext";
import { useFreeAccountTemplates } from "../../queries/misc";
import ContactToSupportModal from "../ContactToSupportModal";
import TemplateChooserModal from "../Navbar/AddAccount/components/TemplateChooserModal";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "components/ui/dropdown-menu";

/**
 * Header workspace/account switcher for the unified app shell.
 *
 * Faithful presentation port of the now-removed legacy Navbar's
 * account/workspace dropdown (the `groupBtn-z-r` block) from a custom
 * inline-div menu onto the shadcn (Radix) DropdownMenu. ALL behaviour is
 * preserved byte-for-byte:
 *
 *  - Trigger shows the active workspace label (`selectedItem.business_name`),
 *    falling back to "Switch Account" exactly as Navbar does.
 *  - Each workspace item runs the same `handleItemClick` -> `handleSwicthAccount`
 *    (note the original spelling) -> `saveActiveLocation` + `checkLocation(...,
 *    "stand_alone")`, then navigates to `/app/chat` and reloads.
 *  - The admin-gated "Add Workspace +" item runs the same `handleNavigate`
 *    free/paid limit checks; when blocked it raises the workspace-limit modal
 *    (the existing `ContactToSupportModal`) or routes to Pricing, identical to
 *    Navbar's `userLimitReached` effect.
 *  - The add flow reuses the existing `TemplateChooserModal` (imported, not
 *    rebuilt) and the per-agency Customization colours are applied to the
 *    trigger via inline style, preserved exactly.
 *  - Entire switcher is gated behind `!checkIfGhlUser`, same as Navbar.
 *
 * The component reads the same context/hooks Navbar reads (`useUserInfo`,
 * `useUsersContext`, `useAppServices`, `useNavigate`) so behaviour is identical
 * with no prop wiring required; it takes no props.
 */
export default function WorkspaceSwitcher() {
  const navigate = useNavigate();

  // useUserInfo() is a positional array (see context/user.js). Mirror the exact
  // indices Navbar consumes.
  const userData = useUserInfo()[0];
  const activeLocation = useUserInfo()[4];
  const saveActiveLocation = useUserInfo()[5];
  const userLocations = useUserInfo()[8] || [];
  const checkIfGhlUser = useUserInfo()[9];
  const agencyFromUser = useUserInfo()[11];
  const checkLocation = useUserInfo()[15];
  const isUserLocationsLoading = useUserInfo()[23];

  const { agency, products, isHighestPlan, fetchChats, fetchTasksData } = useUsersContext();
  const queryClient = useQueryClient();

  const agencyData =
    agency && Object.keys(agency)?.length > 0 ? agency : agencyFromUser;

  const [selectedItem, setSelectedItem] = useState(activeLocation);
  const [userLimitReached, setUserLimitReached] = useState(false);
  const [openLimitModal, setOpenLimitModal] = useState(false);
  const [showTemplateChooser, setShowTemplateChooser] = useState(false);
  const [templates, setTemplates] = useState([]);

  // Keep the trigger label in sync with the active location, mirroring Navbar's
  // effect (including the "no active location -> adopt the first" fallback).
  useEffect(() => {
    if (activeLocation) {
      setSelectedItem(activeLocation);
    } else if (!activeLocation && userLocations.length > 0) {
      setSelectedItem(userLocations[0]);
      saveActiveLocation && saveActiveLocation(userLocations[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLocation, userLocations]);

  // products already carries this agency's saas plans — fetched once by
  // UsersProvider's getSaasProducts() — so no need for a second
  // /saas/agencysaas call just to read additional_accounts off the plan.
  const activeLocationPlan = products?.find(
    (item) => item._id === activeLocation?.plan_id
  );

  const handleSwicthAccount = (location) => {
    saveActiveLocation(location);
    // Not awaited: this only sets onboarding/plugin-gate state (isConnected,
    // agentShow, accountPlugins) that nothing below reads, so it must not be
    // able to delay — or, if it ever hangs, block entirely — the actual
    // account-switch refresh below.
    checkLocation(
      location?.id,
      userData?.email,
      userData?.userName,
      "stand_alone"
    );
    // Reload used to brute-force every query/state back to fresh on switch.
    // Replace it with: drop every react-query cache entry so each one
    // refetches under the new location/agency, plus the two pieces of
    // UsersContext state that are both outside react-query and always
    // mounted in the app chrome (chat sidebar, AppNav's tasks badge) so
    // they wouldn't otherwise refetch just because navigate("/app/chat")
    // targets a route that's already mounted. Pass the new location
    // explicitly — UsersContext's own closure still has the pre-switch
    // location until it re-renders. None of these three are awaited or
    // chained off each other: they're independent refreshes, and one
    // being slow (or, worst case, a hung request with no server-side
    // timeout) must not stall the others.
    queryClient.invalidateQueries();
    fetchChats(0, location?.id);
    fetchTasksData(location?.id, location?.agency_id);
  };

  const handleItemClick = (item) => {
    setSelectedItem(item.business_name);
    handleSwicthAccount(item);
    setTimeout(() => {
      const element = document.getElementById("appendsavelocation");
      if (element) {
        element.innerHTML = item.business_name;
      }
    }, 1000);
    navigate("/app/chat");
  };

  const freeAccountTemplatesMutation = useFreeAccountTemplates();
  const handleNavigate = async () => {
    if (
      userLocations.length - 1 >=
        activeLocation.number_of_additional_accounts &&
      activeLocation?.type === "free"
    ) {
      setUserLimitReached(true);
      return;
    }


    const currentAdditionalAccounts = userLocations.length - 1;
    const maxAdditionalAccounts = activeLocationPlan?.additional_accounts || 0;

    if (
      activeLocation?.type !== "free" &&
      currentAdditionalAccounts >= maxAdditionalAccounts
    ) {
      setUserLimitReached(true);
      return;
    }

    const agencyIdForTemplates =
      activeLocation?.agency_id ||
      agencyData?._id ||
      userData?.agency_id;

    if (agencyIdForTemplates) {
      try {
        const response = await freeAccountTemplatesMutation.mutateAsync(agencyIdForTemplates);
        if (response) setTemplates(response.data || []);
      } catch (err) {
        console.log(err);
      }
    }
    setShowTemplateChooser(true);
  };

  const handleTemplateChoice = (template) => {
    setShowTemplateChooser(false);
    navigate("/app/add-account-setup", {
      state: { templateData: template },
    });
  };

  useEffect(() => {
    const activePlan = products?.find((item) => item?.activePlan === true);
    if (userLimitReached) {
      setTimeout(() => {
        if (
          activeLocation?.type === "free" ||
          (!activePlan?.isenable_account && !activePlan?.developer_plan) ||
          isHighestPlan()
        )
          setOpenLimitModal(true);
        else
          navigate("/app/settings", {
            state: { tab: "Pricing", text: "Upgrade to add more workspaces" },
          });
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLimitReached]);

  if (checkIfGhlUser) return null;

  const triggerLabel =
    selectedItem && typeof selectedItem === "object"
      ? selectedItem?.business_name
      : selectedItem || "Switch Account";

  // Per-agency Customization colours, preserved exactly as Navbar applies them.
  // When a field is unset we fall through to the Lovable interactive-chip
  // styling on the className (soft border, neutral background) rather than the
  // old hardcoded white/black, which rendered a harsh black border.
  const triggerStyle = {
    background: agencyData?.Customization?.button_bg || undefined,
    color: agencyData?.Customization?.button_text || undefined,
    borderColor: agencyData?.Customization?.button_border || undefined,
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            style={triggerStyle}
            className="inline-flex h-9 max-w-[14rem] items-center gap-2 rounded-md border border-border bg-background text-foreground transition-colors hover:bg-muted pl-3 pr-2 text-sm"
          >
            <span
              id="appendsavelocation"
              className="truncate text-left font-medium"
            >
              {triggerLabel}
            </span>
            <ChevronDown
              className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="max-h-[200px] min-w-[var(--radix-dropdown-menu-trigger-width)] w-auto overflow-y-auto"
        >
          {isUserLocationsLoading ? (
            <div className="flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : (
            userLocations.length > 0 &&
            userLocations.map((item, index) => (
              <DropdownMenuItem
                key={index}
                className="cursor-pointer"
                onClick={() => handleItemClick(item)}
              >
                {item.business_name}
              </DropdownMenuItem>
            ))
          )}

          {userData?.role === "admin" && (
            <DropdownMenuItem
              className="cursor-pointer"
              onSelect={(e) => e.preventDefault()}
              onClick={() => handleNavigate()}
            >
              Add Workspace +
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ContactToSupportModal
        isOpen={openLimitModal}
        onClose={() => setOpenLimitModal(false)}
      />
      <TemplateChooserModal
        open={showTemplateChooser}
        onClose={() => setShowTemplateChooser(false)}
        templates={templates}
        onContinue={handleTemplateChoice}
      />
    </>
  );
}
